/* Owns every HTTP call the application shell makes: the session bootstrap, the
   language write, logout, the StatusStrip's health/server reads and the two
   actions the strip offers (config reload, license IP reset), plus the Ctrl+K
   lookup. It is to `app/` what `features/<name>/api.ts` is to a feature —
   the only module under `app/` allowed to import `lib/api.ts` (PANEL.md §11,
   §14). Shell components never call `fetch` and never build a key inline. */

import type {
  BanDto,
  CommandResponse,
  DetectionDto,
  LicenseResponse,
  Locale,
  LookupResponse,
  MeResponse,
  Paginated,
  ServerDto,
  ServerResponse,
  StatsHealth,
  UserResponse,
} from '@sentinelac/shared'
import { queryOptions } from '@tanstack/react-query'

import { api, newIdempotencyKey, refreshAccessToken } from '../lib/api.js'
import { getAccessToken, setServers, setSession, setUser } from '../lib/auth-store.js'
import { qk, REFETCH_INTERVAL, STALE } from '../lib/queryKeys.js'

/**
 * The boot read behind `RequireAuth`. A reload has no access token — it lives in
 * memory only (SPEC §10.3) — so the refresh cookie has to mint one first. When
 * there is no valid `sac_rt` this rejects, which is exactly how the guard learns
 * the visitor is anonymous.
 */
export async function bootstrapSession(): Promise<MeResponse> {
  if (!getAccessToken()) {
    const accessToken = await refreshAccessToken()
    // `refreshAccessToken` already wrote the token and the user; `/auth/me` adds
    // the server projection the switcher and the permission checks need.
    const me = await api<MeResponse>('/auth/me')
    setSession({ accessToken, user: me.user })
    setServers(me.servers)
    return me
  }
  const me = await api<MeResponse>('/auth/me')
  setUser(me.user)
  setServers(me.servers)
  return me
}

export const meQueryOptions = () =>
  queryOptions({
    queryKey: qk.me(),
    queryFn: bootstrapSession,
    staleTime: STALE.me,
    // A failed bootstrap means "no session", not "flaky network": retrying only
    // delays the redirect to /login.
    retry: false,
  })

export const healthQueryOptions = (serverId: string) =>
  queryOptions({
    queryKey: qk.health(serverId),
    queryFn: () => api<StatsHealth>(`/servers/${serverId}/stats/health`),
    staleTime: STALE.health,
    // The WS `server.status` message is the primary source; this interval is the
    // fallback for a socket that is down or still reconnecting (PANEL.md §11).
    refetchInterval: REFETCH_INTERVAL.health,
  })

export const serverQueryOptions = (serverId: string) =>
  queryOptions({
    queryKey: qk.server(serverId),
    queryFn: async () => (await api<ServerResponse>(`/servers/${serverId}`)).server,
    staleTime: STALE.default,
  })

/**
 * The palette's identifier search. `enabled` is the caller's business: the query
 * only ever runs with an expression `lib/identifiers.ts` has already normalised,
 * so a half-typed value never burns the 60/min budget.
 */
export const lookupQueryOptions = (expression: string) =>
  queryOptions({
    queryKey: qk.lookup(expression),
    queryFn: () => api<LookupResponse>('/lookup', { query: { identifier: expression } }),
    staleTime: STALE.lookup,
  })

/**
 * API.md §21.5 — the hub is fire-and-forget, so after every `hello` the client
 * re-reads what it may have missed. `from` is the newest row the ring buffer
 * still holds; without one the socket is new and the last 100 rows are the seed.
 */
export async function backfillDetections(serverId: string, from: string | undefined): Promise<Paginated<DetectionDto>> {
  return api<Paginated<DetectionDto>>(`/servers/${serverId}/detections`, {
    query: from ? { limit: 100, from } : { limit: 100 },
  })
}

export async function backfillActiveBans(serverId: string): Promise<Paginated<BanDto>> {
  return api<Paginated<BanDto>>(`/servers/${serverId}/bans`, { query: { limit: 20, status: 'active' } })
}

export async function patchLocale(locale: Locale): Promise<UserResponse> {
  return api<UserResponse>('/users/me', { method: 'PATCH', body: { locale } })
}

export async function logout(): Promise<void> {
  await api<void>('/auth/logout', { method: 'POST', body: {} })
}

/** `POST …/commands {type:'reloadConfig'}` — the StatusStrip and AgentHealthCard action. */
export async function requestConfigReload(serverId: string): Promise<CommandResponse> {
  return api<CommandResponse>(`/servers/${serverId}/commands`, {
    method: 'POST',
    body: { type: 'reloadConfig' },
    // API.md §1.7: a double-click must not queue the same reload twice.
    idempotencyKey: newIdempotencyKey(),
  })
}

/** `POST …/licenses/:licenseId/reset-ip` — 3/day per license, so a 429 is expected UX. */
export async function resetLicenseIp(serverId: string, licenseId: string): Promise<LicenseResponse> {
  return api<LicenseResponse>(`/servers/${serverId}/licenses/${licenseId}/reset-ip`, {
    method: 'POST',
    body: {},
  })
}

export type { ServerDto }
